
import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { toast } from "sonner";

export const LeadAnalytics = () => {
  const { data: leadStats, isLoading: isLoadingLeads, error: leadError } = useQuery({
    queryKey: ["lead-stats"],
    queryFn: async () => {
      const { data: leads, error } = await supabase
        .from("leads")
        .select("created_at, status")
        .order("created_at");
      
      if (error) throw error;

      // If no data, create some sample data
      if (!leads || leads.length === 0) {
        console.warn("No lead data found, creating sample leads");
        await createSampleLeads();
        const { data: newLeads, error: newError } = await supabase
          .from("leads")
          .select("created_at, status")
          .order("created_at");
          
        if (newError) throw newError;
        
        if (!newLeads || newLeads.length === 0) {
          return [];
        }
        
        return processLeadData(newLeads);
      }

      return processLeadData(leads);
    },
  });

  const { data: conversionRates, isLoading: isLoadingRates, error: ratesError } = useQuery({
    queryKey: ["conversion-rates"],
    queryFn: async () => {
      const { data: leads, error } = await supabase
        .from("leads")
        .select("status");
      
      if (error) throw error;

      const total = leads?.length || 0;
      if (total === 0) return { qualificationRate: 0, conversionRate: 0 };
      
      const qualified = leads?.filter(l => l.status === "qualified").length || 0;
      const converted = leads?.filter(l => l.status === "converted").length || 0;

      return {
        qualificationRate: total > 0 ? (qualified / total) * 100 : 0,
        conversionRate: qualified > 0 ? (converted / qualified) * 100 : 0,
      };
    },
  });

  // Process lead data for visualization
  const processLeadData = (leads: any[]) => {
    const stats = leads.reduce((acc: any, lead) => {
      const date = new Date(lead.created_at).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = { date, total: 0, qualified: 0, converted: 0 };
      }
      acc[date].total++;
      if (lead.status === "qualified") acc[date].qualified++;
      if (lead.status === "converted") acc[date].converted++;
      return acc;
    }, {});

    return Object.values(stats);
  };

  // Create sample lead data
  const createSampleLeads = async () => {
    try {
      const statuses = ["new", "qualified", "converted", "disqualified"];
      const sampleLeads = [];
      
      // Generate 100 sample leads over the last 30 days
      const now = new Date();
      for (let i = 0; i < 100; i++) {
        const daysAgo = Math.floor(Math.random() * 30);
        const createdAt = new Date();
        createdAt.setDate(now.getDate() - daysAgo);
        
        sampleLeads.push({
          name: `Sample Lead ${i + 1}`,
          email: `lead${i + 1}@example.com`,
          company: `Company ${i + 1}`,
          status: statuses[Math.floor(Math.random() * statuses.length)],
          source: ["website", "referral", "social media", "email campaign"][Math.floor(Math.random() * 4)],
          created_at: createdAt.toISOString(),
          lead_score: Math.floor(Math.random() * 100)
        });
      }
      
      // Insert sample leads in batches to avoid request size limits
      const batchSize = 25;
      for (let i = 0; i < sampleLeads.length; i += batchSize) {
        const batch = sampleLeads.slice(i, i + batchSize);
        const { error } = await supabase.from("leads").insert(batch);
        if (error) throw error;
      }
      
    } catch (error) {
      console.error("Error creating sample leads:", error);
      toast.error("Failed to create sample leads");
    }
  };

  // Handle data loading errors
  useEffect(() => {
    if (leadError) {
      console.error("Error loading lead statistics:", leadError);
      toast.error("Failed to load lead statistics");
    }
    
    if (ratesError) {
      console.error("Error loading conversion rates:", ratesError);
      toast.error("Failed to load conversion rates");
    }
  }, [leadError, ratesError]);

  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042'];

  // Prepare source distribution data for pie chart
  const { data: sourcesData, isLoading: isLoadingSources } = useQuery({
    queryKey: ["lead-sources"],
    queryFn: async () => {
      const { data: leads, error } = await supabase
        .from("leads")
        .select("source");
      
      if (error) throw error;

      const sources = leads?.reduce((acc: Record<string, number>, lead) => {
        const source = lead.source || "unknown";
        acc[source] = (acc[source] || 0) + 1;
        return acc;
      }, {});

      return Object.entries(sources || {}).map(([name, value]) => ({ name, value }));
    }
  });

  if (isLoadingLeads || isLoadingRates || isLoadingSources) {
    return <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
    </div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Lead Generation Trend</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={leadStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="total" stroke="#8884d8" name="Total Leads" />
                <Line type="monotone" dataKey="qualified" stroke="#82ca9d" name="Qualified" />
                <Line type="monotone" dataKey="converted" stroke="#ffc658" name="Converted" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Conversion Metrics</h3>
          <div className="space-y-6">
            <div>
              <div className="flex justify-between mb-2">
                <span>Qualification Rate</span>
                <span>{conversionRates?.qualificationRate.toFixed(1)}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 rounded-full"
                  style={{ width: `${conversionRates?.qualificationRate || 0}%` }}
                />
              </div>
            </div>
            
            <div>
              <div className="flex justify-between mb-2">
                <span>Conversion Rate</span>
                <span>{conversionRates?.conversionRate.toFixed(1)}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 rounded-full"
                  style={{ width: `${conversionRates?.conversionRate || 0}%` }}
                />
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Lead Sources Distribution</h3>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={sourcesData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {sourcesData?.map((_entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${value} leads`, 'Count']} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
};
